import { useCallback, type ReactNode } from "react";
import { Checkbox, FormControlLabel, Stack, Typography } from "@mui/material";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import {
  EXPORT_FIELDS_INCLUSION_TYPES,
  type ExportFieldConfiguration,
} from "./types";
import {
  defaultFirmographicsFields,
  type ExportFieldOption,
} from "./exportConfigurationFields";
import { FieldsAutocomplete } from "./FieldsAutocomplete";

const INCLUSION_CYCLE: Record<
  EXPORT_FIELDS_INCLUSION_TYPES,
  EXPORT_FIELDS_INCLUSION_TYPES
> = {
  [EXPORT_FIELDS_INCLUSION_TYPES.ALL]: EXPORT_FIELDS_INCLUSION_TYPES.SELECTED,
  [EXPORT_FIELDS_INCLUSION_TYPES.SELECTED]: EXPORT_FIELDS_INCLUSION_TYPES.NONE,
  [EXPORT_FIELDS_INCLUSION_TYPES.NONE]: EXPORT_FIELDS_INCLUSION_TYPES.ALL,
};

interface Props {
  fields: ExportFieldOption[];
  value: ExportFieldConfiguration;
  onChange: (cfg: ExportFieldConfiguration) => void;
  title?: string;
  checkboxLabel?: string;
  requiredField?: { values: string[]; note?: string };
  firmographicsFields?: ExportFieldOption[];
  firmographicsCheckboxLabel?: string;
  requiredFirmographicsField?: { value: string; note: string };
  renderFieldSelector?: (
    selectedItems: string[],
    onSelectItems: (ids: string[]) => void
  ) => ReactNode;
  disabled?: boolean;
}

export function FieldConfigurationSelector({
  fields,
  value,
  onChange,
  title,
  checkboxLabel = "Export all fields",
  requiredField,
  firmographicsFields,
  firmographicsCheckboxLabel = "Export All Firmographics Data",
  requiredFirmographicsField,
  renderFieldSelector,
  disabled = false,
}: Props) {
  const withRequiredMainField = useCallback(
    (fieldsList: string[]) => {
      if (!requiredField) return fieldsList;
      const missing = requiredField.values.filter(
        (v) => !fieldsList.includes(v)
      );
      return missing.length > 0 ? [...fieldsList, ...missing] : fieldsList;
    },
    [requiredField]
  );

  const withRequiredField = useCallback(
    (fieldsList: string[]) =>
      requiredFirmographicsField &&
      !fieldsList.includes(requiredFirmographicsField.value)
        ? [...fieldsList, requiredFirmographicsField.value]
        : fieldsList,
    [requiredFirmographicsField]
  );

  const handleCheckboxChange = useCallback(() => {
    const next = INCLUSION_CYCLE[value.inclusionType];
    const fieldsForNext =
      next === EXPORT_FIELDS_INCLUSION_TYPES.NONE
        ? []
        : next === EXPORT_FIELDS_INCLUSION_TYPES.SELECTED
          ? withRequiredMainField(value.fields)
          : value.fields;
    onChange({ ...value, inclusionType: next, fields: fieldsForNext });
  }, [value, onChange, withRequiredMainField]);

  const handleFirmographicsCheckboxChange = useCallback(() => {
    const current =
      value.firmographicsInclusionType ?? EXPORT_FIELDS_INCLUSION_TYPES.ALL;
    const next = INCLUSION_CYCLE[current];
    const fieldsForNext =
      next === EXPORT_FIELDS_INCLUSION_TYPES.SELECTED
        ? withRequiredField(defaultFirmographicsFields)
        : [];
    onChange({
      ...value,
      firmographicsInclusionType: next,
      firmographicsFields: fieldsForNext,
    });
  }, [value, onChange, withRequiredField]);

  const isSelected =
    value.inclusionType === EXPORT_FIELDS_INCLUSION_TYPES.SELECTED;
  const firmographicsInclusionType =
    value.firmographicsInclusionType ?? EXPORT_FIELDS_INCLUSION_TYPES.ALL;
  const isFirmographicsSelected =
    firmographicsInclusionType === EXPORT_FIELDS_INCLUSION_TYPES.SELECTED;

  return (
    <Stack spacing={1}>
      {title && (
        <Typography variant="subtitle2" fontWeight={600}>
          {title}
        </Typography>
      )}
      <FormControlLabel
        label={checkboxLabel}
        control={
          <Checkbox
            size="small"
            checked={value.inclusionType === EXPORT_FIELDS_INCLUSION_TYPES.ALL}
            indeterminate={isSelected}
            indeterminateIcon={
              <IndeterminateCheckBoxIcon fontSize="small" color="primary" />
            }
            onChange={handleCheckboxChange}
            disabled={disabled}
            data-testid="field-config-inclusion-checkbox"
          />
        }
      />

      {isSelected &&
        (renderFieldSelector ? (
          renderFieldSelector(value.fields, (ids) =>
            onChange({ ...value, fields: ids })
          )
        ) : (
          <FieldsAutocomplete
            id="field-config"
            options={fields}
            selectedValues={value.fields}
            onChange={(ids) =>
              onChange({ ...value, fields: withRequiredMainField(ids) })
            }
            placeholder="Search fields..."
            disabled={disabled}
          />
        ))}
      {requiredField?.note && (
        <Typography variant="caption" color="text.secondary">
          {requiredField.note}
        </Typography>
      )}

      {firmographicsFields && firmographicsFields.length > 0 && (
        <Stack spacing={1} sx={{ mt: 1 }}>
          <Typography variant="subtitle2">Firmographics Fields</Typography>
          <FormControlLabel
            label={firmographicsCheckboxLabel}
            control={
              <Checkbox
                size="small"
                checked={
                  firmographicsInclusionType ===
                  EXPORT_FIELDS_INCLUSION_TYPES.ALL
                }
                indeterminate={isFirmographicsSelected}
                indeterminateIcon={
                  <IndeterminateCheckBoxIcon fontSize="small" color="primary" />
                }
                onChange={handleFirmographicsCheckboxChange}
                disabled={disabled}
                data-testid="firmographics-field-config-inclusion-checkbox"
              />
            }
          />

          {isFirmographicsSelected && (
            <FieldsAutocomplete
              id="firmographics-field-config"
              options={firmographicsFields}
              selectedValues={value.firmographicsFields ?? []}
              onChange={(ids) =>
                onChange({
                  ...value,
                  firmographicsFields: withRequiredField(ids),
                })
              }
              placeholder="Search firmographic fields..."
              disabled={disabled}
            />
          )}
          {requiredFirmographicsField && (
            <Typography variant="caption" color="text.secondary">
              {requiredFirmographicsField.note}
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
}
